import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ExameDetalhes.css';

import { API_URL } from '../config';
import { formatDate } from '../utils/formatDate';

function OrcamentoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orcamento, setOrcamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('resumo');

  useEffect(() => {
    fetchOrcamentoDetalhes();
  }, [id]);

  const fetchOrcamentoDetalhes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/orcamentos/${id}`);
      setOrcamento(response.data);
    } catch (err) {
      setError('Erro ao carregar dados do orçamento: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatarValor = (value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    } else if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    } else if (typeof value === 'string') {
      if (value === 'T' || value === 't') return 'Sim';
      if (value === 'F' || value === 'f') return 'Não';
      return value;
    } else if (typeof value === 'number') {
      return value.toString();
    } else {
      return String(value);
    }
  };

  // formatDate importado de ../utils/formatDate

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loading) {
    return <div className="loading">Carregando detalhes do orçamento...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error">{error}</p>
        <button onClick={() => navigate('/orcamentos')} className="back-button">
          Voltar para Orçamentos
        </button>
      </div>
    );
  }

  if (!orcamento) {
    return <div className="loading">Orçamento não encontrado</div>;
  }

  // Definir abas disponíveis
  const abas = [
    { id: 'resumo', label: '📋 Resumo', show: true },
    { id: 'paciente', label: '👤 Dados do Paciente', show: true },
    { id: 'itens', label: '🧪 Itens do Orçamento', show: orcamento.itens && orcamento.itens.length > 0 },
    { id: 'valores', label: '💰 Valores e Descontos', show: true },
    { id: 'descontos', label: '🏷️ Descontos Concedidos', show: orcamento.descontos && orcamento.descontos.length > 0 },
    { id: 'usuarios', label: '👥 Usuários', show: orcamento.usuarios && orcamento.usuarios.length > 0 },
    { id: 'informacoes', label: 'ℹ️ Informações Adicionais', show: true }
  ].filter(aba => aba.show);

  return (
    <div className="exame-detalhes-page">
      <div className="page-header-detalhes">
        <button onClick={() => navigate('/orcamentos')} className="back-button">
          ← Voltar
        </button>
        <h1>💰 Orçamento #{orcamento.ID}</h1>
        {orcamento.PACIENTE && (
          <button
            onClick={() => navigate(`/pacientes/${orcamento.PACIENTE}`)}
            className="btn-detail-primary"
          >
            Ver Paciente
          </button>
        )}
      </div>

      {/* Sistema de Abas */}
      <div className="detail-tabs">
        {abas.map(aba => (
          <button
            key={aba.id}
            onClick={() => setAbaAtiva(aba.id)}
            className={`detail-tab ${abaAtiva === aba.id ? 'active' : ''}`}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Aba: Resumo */}
      {abaAtiva === 'resumo' && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          border: '2px solid #e5e7eb'
        }}>
          <h2 style={{ marginTop: 0, color: '#333' }}>Resumo do Orçamento</h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginTop: '1.5rem'
          }}>
            <div>
              <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                📅 Data do Orçamento
              </label>
              <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
                {formatDate(orcamento.DATA)}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                👤 Paciente
              </label>
              <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
                {orcamento.NOME_PACIENTE || orcamento.PACIENTE_NOME_COMPLETO || '-'}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                🏥 Convênio
              </label>
              <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>
                {orcamento.CONVENIO || '-'} {orcamento.PLANO && `(${orcamento.PLANO})`}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                💰 Valor Total
              </label>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#333' }}>
                {formatCurrency(orcamento.TOTAL)}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                🏷️ Desconto
              </label>
              <div style={{ fontSize: '1.1rem', fontWeight: 500, color: '#ef4444' }}>
                {formatCurrency(orcamento.DESCONTO)}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                ✓ Status
              </label>
              <div>
                <span style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  background: orcamento.CONVERTIDO ? '#d1fae5' : '#fee2e2',
                  color: orcamento.CONVERTIDO ? '#059669' : '#dc2626',
                  display: 'inline-block'
                }}>
                  {orcamento.CONVERTIDO ? '✓ Convertido' : '✗ Não Convertido'}
                </span>
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          {orcamento.estatisticas && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem',
              marginTop: '2rem',
              paddingTop: '2rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div className="stat-card">
                <div className="stat-label">Total de Itens</div>
                <div className="stat-value">{orcamento.estatisticas.totalItens}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Descontos</div>
                <div className="stat-value">{orcamento.estatisticas.totalDescontos}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Usuários</div>
                <div className="stat-value">{orcamento.estatisticas.totalUsuarios}</div>
              </div>
            </div>
          )}

          {orcamento.OBSERVACAO && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
              <label style={{ fontWeight: 600, color: '#6b7280', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                📝 Observação
              </label>
              <div style={{ fontSize: '1rem' }}>
                {orcamento.OBSERVACAO}
              </div>
            </div>
          )}

          {orcamento.MOTIVO_PERDA_DESC && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fee2e2', borderRadius: '8px', border: '1px solid #fecaca' }}>
              <label style={{ fontWeight: 600, color: '#991b1b', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                ❌ Motivo de Perda
              </label>
              <div style={{ fontSize: '1rem', color: '#7f1d1d' }}>
                {orcamento.MOTIVO_PERDA_DESC}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Aba: Dados do Paciente */}
      {abaAtiva === 'paciente' && (
        <div className="detalhes-grid">
          <div className="info-card">
            <h3>👤 Informações do Paciente</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Nome:</label>
                <span>{orcamento.NOME_PACIENTE || orcamento.PACIENTE_NOME_COMPLETO || '-'}</span>
              </div>
              <div className="info-item">
                <label>CPF:</label>
                <span>{orcamento.PACIENTE_CPF || '-'}</span>
              </div>
              <div className="info-item">
                <label>Data de Nascimento:</label>
                <span>{formatDate(orcamento.PACIENTE_DATANASCIMENTO)}</span>
              </div>
              <div className="info-item">
                <label>Sexo:</label>
                <span>{orcamento.PACIENTE_SEXO || '-'}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{orcamento.EMAIL || orcamento.PACIENTE_EMAIL_PRINCIPAL || '-'}</span>
              </div>
              <div className="info-item">
                <label>Celular:</label>
                <span>{orcamento.CELULAR || '-'}</span>
              </div>
              <div className="info-item">
                <label>Telefone:</label>
                <span>{orcamento.TELEFONE || orcamento.PACIENTE_TELEFONE_PRINCIPAL || '-'}</span>
              </div>
              <div className="info-item full-width">
                <label>Endereço:</label>
                <span>{[
                  orcamento.PACIENTE_ENDERECO,
                  orcamento.NUMERO_ENDERECO ? `Nº ${orcamento.NUMERO_ENDERECO}` : null,
                  orcamento.PACIENTE_COMPLEMENTO,
                  orcamento.PACIENTE_BAIRRO,
                  orcamento.PACIENTE_CIDADE && orcamento.PACIENTE_UF ? `${orcamento.PACIENTE_CIDADE}/${orcamento.PACIENTE_UF}` : null,
                  orcamento.PACIENTE_CEP ? `CEP: ${orcamento.PACIENTE_CEP}` : null
                ].filter(Boolean).join(', ') || '-'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aba: Itens do Orçamento */}
      {abaAtiva === 'itens' && orcamento.itens && orcamento.itens.length > 0 && (
        <div className="detalhes-grid">
          <div className="info-card" style={{ gridColumn: '1 / -1' }}>
            <h3>🧪 Itens do Orçamento ({orcamento.itens.length})</h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ordem</th>
                    <th>Código Exame</th>
                    <th>Descrição</th>
                    <th>Tipo</th>
                    <th>Material</th>
                    <th>Quantidade</th>
                    <th>Valor Unit.</th>
                    <th>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orcamento.itens.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.ORDEM}</td>
                      <td><strong>{item.EXAME}</strong></td>
                      <td>{item.LAUDO_DESCRICAO || item.EXAME_DESCRICAO || '-'}</td>
                      <td>{item.EXAME_TIPO || '-'}</td>
                      <td>{item.MATERIAL_NOME || '-'}</td>
                      <td>{item.QUANTIDADE}</td>
                      <td>{formatCurrency(item.VALOR)}</td>
                      <td><strong>{formatCurrency(item.VALOR * item.QUANTIDADE)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Aba: Valores e Descontos */}
      {abaAtiva === 'valores' && (
        <div className="detalhes-grid">
          <div className="info-card">
            <h3>💰 Valores</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Subtotal:</label>
                <span className="valor">{formatCurrency(orcamento.SUBTOTAL)}</span>
              </div>
              <div className="info-item">
                <label>Desconto:</label>
                <span className="valor" style={{ color: '#ef4444' }}>{formatCurrency(orcamento.DESCONTO)}</span>
              </div>
              <div className="info-item">
                <label>Taxa Extra:</label>
                <span className="valor">{formatCurrency(orcamento.TAXAEXTRA)}</span>
              </div>
              <div className="info-item">
                <label>Total:</label>
                <span className="valor total">{formatCurrency(orcamento.TOTAL)}</span>
              </div>
              <div className="info-item">
                <label>Cobertura:</label>
                <span>{orcamento.COBERTURA ? `${orcamento.COBERTURA}%` : '-'}</span>
              </div>
              <div className="info-item">
                <label>Considera Cobertura Plano:</label>
                <span>{formatarValor(orcamento.CONSIDERA_COBERTURA_PLANO)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aba: Descontos Concedidos */}
      {abaAtiva === 'descontos' && orcamento.descontos && orcamento.descontos.length > 0 && (
        <div className="detalhes-grid">
          <div className="info-card" style={{ gridColumn: '1 / -1' }}>
            <h3>🏷️ Descontos Concedidos ({orcamento.descontos.length})</h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Desconto (R$)</th>
                    <th>Desconto (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {orcamento.descontos.map((desc, idx) => (
                    <tr key={idx}>
                      <td><strong>{desc.USUARIO || '-'}</strong></td>
                      <td>{formatCurrency(desc.DESCONTO)}</td>
                      <td>{desc.DESCONTO_PERC ? `${desc.DESCONTO_PERC}%` : '-'}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <td>Total:</td>
                    <td colSpan="2">{formatCurrency(orcamento.estatisticas?.valorTotalDescontos || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Aba: Usuários */}
      {abaAtiva === 'usuarios' && orcamento.usuarios && orcamento.usuarios.length > 0 && (
        <div className="detalhes-grid">
          <div className="info-card" style={{ gridColumn: '1 / -1' }}>
            <h3>👥 Usuários ({orcamento.usuarios.length})</h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {orcamento.usuarios.map((user, idx) => (
                    <tr key={idx}>
                      <td><strong>{user.USUARIO || '-'}</strong></td>
                      <td>{user.TIPO_USUARIO === 0 ? 'Criador' : user.TIPO_USUARIO === 1 ? 'Editor' : user.TIPO_USUARIO === 2 ? 'Visualizador' : user.TIPO_USUARIO}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Aba: Informações Adicionais */}
      {abaAtiva === 'informacoes' && (
        <div className="detalhes-grid">
          <div className="info-card">
            <h3>ℹ️ Informações Adicionais</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Data de Emissão:</label>
                <span>{formatDate(orcamento.DATA_EMISSAO)}</span>
              </div>
              <div className="info-item">
                <label>Data de Vencimento:</label>
                <span>{formatDate(orcamento.DATA_VENCIMENTO)}</span>
              </div>
              <div className="info-item">
                <label>Data de Contato:</label>
                <span>{formatDate(orcamento.DATA_CONTATO)}</span>
              </div>
              <div className="info-item">
                <label>Dias de Validade:</label>
                <span>{orcamento.DIAS_VALIDADE || '-'}</span>
              </div>
              <div className="info-item">
                <label>Local de Coleta:</label>
                <span>{orcamento.LOCAL_COLETA || '-'}</span>
              </div>
              <div className="info-item">
                <label>Prestador:</label>
                <span>{orcamento.PRESTADOR || '-'}</span>
              </div>
              <div className="info-item">
                <label>Tipo de Paciente:</label>
                <span>{orcamento.TIPO_PACIENTE === 'C' ? 'Cliente' : formatarValor(orcamento.TIPO_PACIENTE)}</span>
              </div>
              <div className="info-item">
                <label>Origem:</label>
                <span>{orcamento.ORIGEM || '-'}</span>
              </div>
              <div className="info-item">
                <label>Origem Atendimento:</label>
                <span>{orcamento.ORIGEM_ATENDIMENTO || '-'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrcamentoDetalhes;
